<template>
  <div :class="{visible: showChat}" class="video-room-chat">
    <div class="video-room-chat__header">
      {{ $t('chatTitle') }}
    </div>
    <div class="video-room-chat__bottom-wrap">
      <div ref="wrap" class="video-room-chat__messages-wrap">
        <div v-if="computedMessages" class="video-room-chat__item">
          <div v-for="(messageGroup,index) in computedMessages"
               :key="index" :class="{odd: index%2 === 0}"
          >
            <div class="video-room-chat__name">
              {{ messageGroup.publisher.name }}
            </div>
            <VideoRoomChatMessage v-for="message in
              messageGroup.messages" :key="message._id" :odd="index%2 === 0" :message="message"
            />
          </div>
        </div>
      </div>
      <div class="video-room-chat__textarea-wrap">
        <textarea v-model="messageText" class="video-room-chat__textarea"
                  :placeholder="$t('placeholder')"
        />
        <button class="video-room-chat__btn" @click="send">
          {{ $t('send') }}
        </button>
      </div>
    </div>
    <button class="video-room-chat__close" @click="showChat = false">
      <img src="../assets/img/icons/close-chat.svg" width="18" height="18" alt="" />
    </button>
  </div>
</template>

<script>
import useChat from '@/compositions/useChat';
import {
  computed, nextTick, onMounted, ref, watch,
} from '@vue/composition-api';
import VideoRoomChatMessage from '@/components/VideoRoomChatMessage.vue';

export default {
  name: 'VideoRoomChat',
  components: { VideoRoomChatMessage },
  setup() {
    const {
      fetch, messages, addMessage, showChat,
    } = useChat();

    fetch();

    const messageText = ref('');
    const wrap = ref(null);

    function send() {
      if (!messageText.value.trim()) {
        return;
      }
      addMessage({ message: messageText.value });
      messageText.value = '';
    }

    watch(messages, () => {
      nextTick(() => {
        wrap.value.scrollTop = wrap.value.scrollHeight - wrap.value.clientHeight;
      });
    });

    const computedMessages = computed(() => {
      if (!messages.value) return null;

      let lastPublisher = null;
      const messagesProcessed = [];
      messages.value.forEach((m) => {
        if (lastPublisher !== m.publisher.id) {
          messagesProcessed.push({
            publisher: m.publisher,
            messages: [],
          });
          lastPublisher = m.publisher.id;
        }
        messagesProcessed[messagesProcessed.length - 1].messages.push(m);
      });

      return messagesProcessed;
    });

    onMounted(() => {
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Enter' && !e.ctrlKey) {
          send();
        }
      });
    });

    return {
      messages,
      messageText,
      send,
      computedMessages,
      showChat,
      wrap,
    };
  },
};
</script>

<style lang="scss" scoped>
.video-room-chat {
  min-width: 19.0625rem;
  max-width: 19.0625rem;
  max-height: 100vh;
  text-align: left;
  background: #1C1C1E;
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;

  &__header {
    padding: 1rem 1rem 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    font-size: 0.8125rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    letter-spacing: 0.01em;
    flex-shrink: 0;
  }

  &__bottom-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0.75rem 0.75rem 0;
  }

  &__name {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.45);
    margin-bottom: 0.25rem;
    padding: 0 0.25rem;
    letter-spacing: 0.01em;
  }

  &__messages-wrap {
    overflow-y: auto;
    flex: 1;
    padding-bottom: 0.5rem;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.12) transparent;
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.12);
      border-radius: 2px;
    }
  }

  &__textarea-wrap {
    flex-shrink: 0;
    padding: 0.625rem 0 0.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.07);
    margin-top: 0.5rem;
  }

  &__textarea {
    width: 100%;
    min-height: 4rem;
    max-height: 8rem;
    padding: 0.625rem 0.75rem;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 0.625rem;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #FFFFFF;
    margin-bottom: 0.5rem;
    resize: none;
    transition: border-color 0.2s;
    &:focus-visible {
      outline: none;
    }
    &:focus {
      outline: none;
      border-color: rgba(10, 130, 217, 0.5);
      background: rgba(255, 255, 255, 0.08);
    }
    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    width: 100%;
    background: linear-gradient(135deg, #0A82D9 0%, #00CBF3 100%);
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #FFFFFF;
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    transition: box-shadow 0.2s, transform 0.15s;
    box-shadow: 0 2px 8px rgba(10, 130, 217, 0.3);
    &:focus, &:active, &:hover {
      outline: none;
      box-shadow: 0 4px 14px rgba(10, 130, 217, 0.45);
    }
    &:active {
      transform: scale(0.98);
    }
  }

  &__close {
    display: none;
    position: absolute;
    top: 0.875rem;
    right: 0.875rem;
    cursor: pointer;
    width: 1.75rem;
    height: 1.75rem;
    background: rgba(255, 255, 255, 0.08);
    border: none;
    border-radius: 50%;
    display: none;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
    &:focus, &:active, &:hover {
      outline: none;
      background: rgba(255, 255, 255, 0.15);
    }
  }
}

@media (max-width: 1199px) {
  .video-room-chat {
    width: 100%;
    max-width: 22rem;
    height: 100vh;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    border: none;
    z-index: 4;
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translate(100%, 0);
    &.visible {
      transform: translate(0, 0);
    }
    &__close {
      display: flex;
    }
    &__bottom-wrap {
      padding: 0.75rem 0.875rem 0;
    }
    &__textarea {
      font-size: 0.9375rem;
    }
    &__btn {
      font-size: 0.9375rem;
      padding: 0.625rem 1rem;
    }
    &__header {
      padding-right: 3rem;
    }
  }
}

@media (max-width: 599px) {
  .video-room-chat {
    max-width: 100%;
  }
}

@media screen and (device-aspect-ratio: 40/71){
  textarea { font-size: 0.9375rem; }
}
@media screen and (device-aspect-ratio: 375/667) {
  textarea { font-size: 0.9375rem; }
}
@media screen and (device-aspect-ratio: 9/16) {
  textarea { font-size: 0.9375rem; }
}
</style>

<i18n locale="en" lang="yaml">
send: Send
placeholder: Enter your message
chatTitle: Chat
</i18n>

<i18n locale="ru" lang="yaml">
send: Отправить
placeholder: Введите сообщение
chatTitle: Чат
</i18n>
